import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Box, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import * as actionsNN05 from '../../../store/actions/indexNN05';
import { AppState } from '../../../store/rootReducer';
import { TableHeaders } from '../../../models/Headers';

/**
 * Table Header Component Styling System
 * 
 * Defines comprehensive styling for the employee table header with sortable columns,
 * filter inputs, and interactive elements using Material-UI theming.
 * 
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 05.08.2025 
 * 
 * Design Features:
 * - Professional table header appearance with subtle shadows
 * - Interactive sort indicators with color-coded states
 * - Hover effects for enhanced user experience
 * - Accessible color contrast and typography
 * 
 * Style Architecture:
 * - Base cell styling with consistent padding and typography
 * - Sort label integration with custom icon styling
 * - Hover and active state management
 * - Color-coded sort indicators for visual feedback
 * 
 * @returns Style classes object for table header components
 */
// Styles
const useStyles = makeStyles()(() => ({
  /**
   * Comprehensive table cell styling for sortable column headers
   * 
   * Creates professional-looking table headers with interactive sorting capabilities,
   * hover effects, and consistent typography throughout the employee data table.
   * 
   * Base Properties:
   * - width: auto - Flexible width based on content
   * - textTransform: capitalize - Proper case for header labels
   * - boxShadow: Subtle shadow for depth and separation
   * - padding: 6px 20px with 10px right padding for optimal spacing
   * - textAlign: left - Consistent left alignment for readability
   * 
   * Typography:
   * - fontSize: 16px - Optimal size for header readability
   * - fontWeight: 500 - Medium weight for header emphasis
   * - backgroundColor: #fff - Clean white background
   * - color: #575655 - Professional gray text color
   * - borderBottom: 1px solid #c4c4c4 - Subtle separation line
   * 
   * Sort Icon Styling:
   * - opacity: 1 !important - Forces icon visibility
   * - color: #c4c4c4 - Subtle gray for inactive state
   * - Enhanced visibility for better user experience
   * 
   * Interactive States:
   * - Hover: Black color for both label and icon
   * - Active hover: Maintains black color for active elements
   * - Active state: Red color (#d30535) with bold weight for current sort
   * 
   * Accessibility Features:
   * - High contrast color combinations
   * - Clear visual feedback for interactive elements
   * - Consistent hover states across all sortable elements
   * 
   * Usage: Applied to all sortable table header cells
   */
  tableCell: {
    width: 'auto',
    textTransform: 'capitalize',
    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
    marginLeft: 0,
    padding: '6px 20px',
    paddingRight: '10px',
    textAlign: 'left',
    fontSize: 16,
    fontWeight: 500,
    backgroundColor: '#fff',
    color: '#575655',
    height: 40,
    borderBottom: '1px solid #c4c4c4',
    // Make sure the icon is visible
    '& .MuiTableSortLabel-icon': {
      opacity: '1 !important',  // Force visibility of the icon
      color: '#c4c4c4',         // Optional: set a color for the icon
    },
    "& .MuiTableSortLabel-root:hover, .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon": {
      color: "#000"
    },
    "& .MuiTableSortLabel-root.Mui-active:hover, .MuiTableSortLabel-root.Mui-active:hover .MuiTableSortLabel-icon": {
      color: "#000"
    },
    "& .MuiTableSortLabel-root.Mui-active, .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
      fontWeight: 600,
      color: "#d30535",
    }
  },
}));

/**
 * Initial Column Header Configuration for NN05 Employee Table
 * 
 * Defines the complete column structure and default settings for the employee
 * data table, including sorting, filtering, and display properties.
 * 
 * Column Configuration Properties:
 * - foc: Focus state (0=unfocused, 1=focused)
 * - isSort: Sorting capability (0=disabled, 1=enabled)
 * - isActive: Current sort status (0=inactive, 1=active)
 * - isFilter: Filtering capability (0=disabled, 1=enabled)
 * - filter: Current filter value (empty string by default)
 * - vis: Visibility state (0=hidden, 1=visible)
 * - width: Column width percentage for responsive layout
 * - sortDirection: Sort direction ('asc' or 'desc')
 * - id: Unique column identifier for form and state management
 * - lb: Display label for column header
 * 
 * Column Definitions:
 * 1. ansattnr: Employee Number (1.5% width, filterable, sortable)
 * 2. navn: Name (4% width, filterable, default desc sort)
 * 3. fodselsdato: Birth Date (1% width, display only)
 * 4. telefon: Phone Number (4% width, display only)
 * 5. fra: Employment Start Date (10% width, display only)
 * 6. til: Employment End Date (5% width, display only)
 * 7. actions: Action Buttons (4% width, display only)
 * 
 * Design Decisions:
 * - Only Employee Number and Name are filterable for focused searching
 * - All columns are visible by default for comprehensive data view
 * - Responsive width percentages ensure proper table layout
 * - Default ascending sort except for Name (descending for alphabetical browsing)
 * 
 * Usage: Used as initial state and reset target for table configuration
 */
// Initial state for the column headers
// headerListInitialState
export const hInit: TableHeaders[] = [
  { foc: 0, isSort: 0, isActive: 0, isFilter: 1, filter: '', vis: 1, width: '1.5%', sortDirection: 'asc', id: "ansattnr", lb: "Ansatt nr." },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 1, filter: '', vis: 1, width: '4%', sortDirection: 'desc', id: "name", lb: "Navn" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '1%', sortDirection: 'asc', id: "fodselsdato", lb: "Fødselsdato" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '4%', sortDirection: 'asc', id: "telefon", lb: "Telefon" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '10%', sortDirection: 'asc', id: "fra", lb: "Ansatt dato" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '5%', sortDirection: 'asc', id: "til", lb: "Sluttet dato" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '4%', sortDirection: 'asc', id: "actions", lb: "Handlinger" },
];

/**
 * Props interface for NN05TableHead component
 * 
 * Defines the callback interface for communicating sort and filter changes
 * back to the parent component for data processing.
 * 
 * @property onRequestSort - Callback function triggered when sorting or filtering changes
 *                          Receives updated column header configuration array
 */
// Props interface
interface NN05TableHeadProps { onRequestSort: (property: TableHeaders[]) => void; }

/**
 * Form input type definition for filter fields
 * 
 * Defines the structure for form inputs used in column filtering,
 * with dynamic property support for all possible column identifiers.
 * 
 * @property naam - Name filter field (specific type: any for flexibility)
 * @property [key: string] - Dynamic properties for all column filter fields
 */
// Define the form input types
type FormInputs = {
  navn: any
  [key: string]: string // Add a catch-all key of type string
};

/**
 * NN05 Employee Table Header Component
 * 
 * Advanced table header component providing sortable columns, inline filtering,
 * and interactive column management for the employee data table.
 * 
 * Core Features:
 * - Sortable columns with visual sort indicators
 * - Inline text filtering for searchable columns
 * - Interactive filter toggle with animated icons
 * - Form-based filter input management
 * - Redux state integration for persistent column configuration
 * - Responsive column width management
 * 
 * Sorting Functionality:
 * - Click column headers to toggle sort direction (asc/desc)
 * - Visual indicators show current sort column and direction
 * - Only one column can be actively sorted at a time
 * - Sort state persists across component re-renders
 * 
 * Filtering System:
 * - Text-based filtering for Employee Number and Name columns
 * - Filter toggle button with rotation animation
 * - Enter key triggers immediate filter application
 * - Clear filters by clicking column labels
 * - Filter state synchronized with Redux store
 * 
 * Interactive Elements:
 * - Hover effects on sortable headers
 * - Focus management for filter inputs
 * - Tooltip guidance for user interactions
 * - Keyboard navigation support
 * 
 * State Management:
 * - Redux integration for column configuration persistence
 * - React Hook Form for filter input management
 * - Automatic form reset when global reset is triggered
 * - Bidirectional state synchronization
 * 
 * @param onRequestSort - Callback function for communicating changes to parent
 * @returns Interactive table header with sorting and filtering capabilities
 */
// Component
const NN05TableHead: React.FC<NN05TableHeadProps> = ({ onRequestSort }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tableHeaders: TableHeaders[] = useSelector((state) => (state as AppState).NN05.tableHeaders);
  const isReset: boolean = useSelector((state) => (state as AppState).NN05.isReset);
  // React Hook Form configuration with default values
  const methods = useForm<FormInputs>({
    defaultValues: {
      navn: '',
    },
  });

  /**
   * Component state synchronization and form reset management
   * 
   * Manages bidirectional synchronization between Redux column header state
   * and React Hook Form filter values, ensuring consistent state across
   * component re-renders and reset operations.
   * 
   * State Synchronization:
   * - Syncs Redux column header filters to form input values
   * - Handles empty/undefined filter values gracefully
   * - Updates form state for all columns on header changes
   * 
   * Reset Functionality:
   * - Monitors global reset flag from Redux state
   * - Clears reset flag after processing to prevent loops
   * - Coordinates with parent component reset operations
   * 
   * Form Management:
   * - Uses setValue to update individual form fields
   * - Handles dynamic property updates for all column filters
   * - Maintains form state consistency across state changes
   * 
   * Dependencies: [dispatch, headers, isReset]
   * - Re-runs when column headers or reset state changes
   * - Ensures immediate response to state updates
   */
  //
  useEffect(() => {
    tableHeaders.forEach(element => {
      // Always set the value, even if it's empty
      methods.setValue(element.id, element.filter ?? '');
    });
    // Reset the reset flag
    if (isReset) {
      dispatch(actionsNN05.NN05ResetForm(false));
    }
    //
  }, [dispatch, tableHeaders, isReset]);
  /**
   * Handles column header click events for sorting functionality
   * 
   * Processes user clicks on sortable column headers, toggles sort direction,
   * and updates the column configuration state for data sorting.
   * 
   * Sort Logic:
   * - Toggles sort direction for the clicked column (asc ↔ desc)
   * - Sets clicked column as active (isActive: 1)
   * - Deactivates all other columns (isActive: 0)
   * - Resets other columns to default 'desc' direction
   * 
   * State Updates:
   * - Creates new column header configuration array
   * - Updates Redux store with new header state
   * - Triggers parent component data sort via callback
   * 
   * Visual Feedback:
   * - Updates sort indicators in table headers
   * - Provides immediate visual response to user interaction
   * - Maintains consistent sort state display
   * 
   * Data Flow:
   * 1. User clicks sortable column header
   * 2. Component updates local column configuration
   * 3. Redux store receives updated configuration
   * 4. Parent component receives sort request callback
   * 5. Parent triggers data reload with new sort parameters
   * 
   * @param {TableHeaders} property - The column header object that was clicked
   */
  // Handle the click event on the column header
  const handleClick = (property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          isActive: 1,
          sortDirection: row.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        ...row,
        isActive: 0,
        sortDirection: 'desc',
      };
    });
    // Update the redux store with the new column header list
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(filteredColumnHeaderList));

    // Call the parent component to sort the data
    if (onRequestSort) {
      onRequestSort(filteredColumnHeaderList);
    }
  };

  /**
   * Handles filter input field blur events for focus state management
   * 
   * Processes when user clicks away from filter input fields, updating
   * the focus state for proper UI display and interaction handling.
   * 
   * Focus Management:
   * - Clears focus state (foc: 0) when field is empty and loses focus
   * - Maintains focus state for fields with content
   * - Updates Redux store with new focus configuration
   * 
   * UI Behavior:
   * - Triggers visual changes in filter input appearance
   * - Coordinates with label shrink/expand behavior
   * - Maintains consistent focus state across component
   * 
   * State Updates:
   * - Maps through column headers to find matching field
   * - Updates only the affected column's focus state
   * - Preserves all other column properties unchanged
   * 
   * @param {FocusEvent} e - The blur event from the input field
   * @param {TableHeaders} property - The column header configuration object
   */
  // Handle the click event on the column header
  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id && (e.target as HTMLInputElement).value === '') {
        return {
          ...row,
          foc: 0,
        };
      }
      return row;
    });
    // Update the redux store with the new column header list
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(filteredColumnHeaderList));
  };

  /**
   * Handles filter input field focus events for UI state management
   * 
   * Processes when user clicks on or tabs into filter input fields,
   * updating focus state and ensuring proper input field activation.
   * 
   * Focus Activation:
   * - Sets focus state (foc: 1) for the target column
   * - Programmatically focuses the DOM input element
   * - Updates Redux store with new focus configuration
   * 
   * DOM Interaction:
   * - Queries for specific input field by name attribute
   * - Ensures input receives focus even if triggered programmatically
   * - Coordinates between React state and DOM focus state
   * 
   * UI Effects:
   * - Triggers label shrink animation
   * - Activates input field styling
   * - Enables filter input interaction
   * 
   * State Management:
   * - Maps through all column headers
   * - Updates only the target column's focus state
   * - Maintains other column properties unchanged
   * 
   * @param {FocusEvent} e - The focus event from the input field
   * @param {TableHeaders} property - The column header configuration object
   */
  // Handle the click event on the column header
  const handleFieldFocus = (_e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          foc: 1,
        };
      }
      return row;
    });
    // Set the focus on the input field
    const inputField: HTMLInputElement | null = document.querySelector('input[name="' + property.id + '"]');
    if (inputField) {
      inputField.focus();
    }
    // Update the redux store with the new column header list
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(filteredColumnHeaderList));
  };

  /**
   * Handles filter input changes and keyboard events for real-time filtering
   * 
   * Processes user input in filter fields, updating filter values and
   * triggering data filtering when Enter key is pressed.
   * 
   * Input Processing:
   * - Captures both keyboard events and input value changes
   * - Updates React Hook Form state with current input value
   * - Prevents event bubbling and default browser behavior
   * 
   * Filter State Management:
   * - Updates filter value in column header configuration
   * - Syncs changes to Redux store for state persistence
   * - Maintains filter state across component re-renders
   * 
   * Trigger Conditions:
   * - Real-time filter value updates on every keystroke
   * - Data filtering triggered specifically on Enter key press
   * - Immediate visual feedback for current filter state
   * 
   * Event Handling:
   * - stopPropagation: Prevents click events on parent elements
   * - preventDefault: Stops default browser form behavior
   * - Keyboard detection: Distinguishes between Enter and other keys
   * 
   * Data Flow:
   * 1. User types in filter input field
   * 2. Component updates form and Redux state
   * 3. On Enter key, triggers parent component data filtering
   * 4. Parent reloads data with new filter parameters
   * 
   * @param {KeyboardEvent | InputEvent} e - The input or keyboard event
   * @param {TableHeaders} property - The column header configuration object
   */
  // Handle the click event on the column header
  interface HandleChangeEvent extends React.KeyboardEvent<HTMLInputElement> {
    target: HTMLInputElement;
  }

  const handleChange = (
    _e: React.KeyboardEvent<HTMLDivElement>,
    property: TableHeaders
  ): void => {
    const { key } = _e; // Get the key pressed
    const inputValue = (_e.target as HTMLInputElement).value;
    const actionSource = key || inputValue; // Get the key pressed or the value entered in the input field
    methods.setValue(property.id as string, inputValue);
    _e.stopPropagation(); // Prevent the event from bubbling up
    _e.preventDefault(); // Prevent the default action of the event from happening
    //
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          filter: inputValue,
        };
      }
      return row;
    });
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(filteredColumnHeaderList));
    // Update the redux store with the new column header list and submit the request to sort the data
    if (onRequestSort && actionSource === 'Enter') {
      onRequestSort(filteredColumnHeaderList);
    }
  };

  /**
   * Handles label click events for clearing filter values
   * 
   * Processes clicks on column labels to clear current filter values,
   * providing a quick way to reset individual column filters.
   * 
   * Clear Operations:
   * - Resets form input value to empty string
   * - Clears filter value in column header configuration
   * - Removes focus state (foc: 0) from the column
   * - Triggers immediate data reload with cleared filter
   * 
   * State Updates:
   * - Updates React Hook Form field value
   * - Modifies Redux column header state
   * - Triggers parent component data refresh
   * 
   * User Experience:
   * - Provides quick filter reset functionality
   * - Immediate visual feedback with cleared input
   * - Instant data update with filter removed
   * 
   * Integration:
   * - Coordinates with form state management
   * - Updates Redux store for state persistence
   * - Triggers parent component callback for data reload
   * 
   * @param {MouseEvent} e - The click event on the label
   * @param {TableHeaders} property - The column header configuration object
   */
  // Handle the click event on the column header
  const handleLabelClicked = (e: React.MouseEvent, property: TableHeaders) => {
    methods.setValue(property.id as string, '');
    // Update the label state to remove the [X] prefix if it exists
    const updatedHeaders: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          filter: '',
          foc: 0,
        };
      }
      return row;
    });
    // Dispatch the updated headers to the redux store
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(updatedHeaders));
    onRequestSort(updatedHeaders);
  };

  /**
   * Handles filter icon click events for toggle filter input visibility
   * 
   * Processes clicks on the filter icon to toggle filter input focus state,
   * providing visual toggle functionality with animated icon rotation.
   * 
   * Toggle Logic:
   * - Checks current focus state of the target column
   * - Toggles focus state (focused ↔ unfocused)
   * - Clears filter value when toggling from focused to unfocused
   * - Preserves filter value when toggling to focused state
   * 
   * Focus Management:
   * - Programmatically focuses input field when activating filter
   * - Clears form value when deactivating filter
   * - Updates Redux state with new focus configuration
   * 
   * Visual Effects:
   * - Filter icon rotates 180 degrees when active (CSS transition)
   * - Input field appearance changes based on focus state
   * - Immediate visual feedback for toggle state
   * 
   * State Coordination:
   * - Updates column header configuration in Redux
   * - Syncs React Hook Form state with focus changes
   * - Triggers data reload when clearing filter (unfocus)
   * 
   * User Experience:
   * - Intuitive toggle behavior for filter activation
   * - Clear visual indicators for filter state
   * - Smooth animations for state transitions
   * 
   * @param {MouseEvent} e - The click event on the filter icon
   * @param {TableHeaders} property - The column header configuration object
   */
  // Handle the click event on the column header
  interface LabelFilterClickEvent extends React.MouseEvent<SVGSVGElement, MouseEvent> { }

  const handleLabelFilterClicked = (
    e: LabelFilterClickEvent,
    property: TableHeaders
  ): void => {
    //
    const isFocused: boolean = property.foc === 1;
    //
    const updatedHeaders: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          foc: isFocused ? 0 : 1,
          filter: isFocused ? '' : row.filter, // Ensure the `filter` property matches the expected type
        };
      }
      return row;
    });

    if (!isFocused) {
      // Set focus on the input field
      const inputField: HTMLInputElement | null = document.querySelector<HTMLInputElement>(`input[name="${property.id}"]`);
      inputField?.focus();
    } else {
      methods.setValue(property.id as string, '');
    }

    // Dispatch the updated headers to the redux store
    dispatch(actionsNN05.NN05UpdateColumnHeaderList(updatedHeaders));
    // Call the parent component to sort the data
    if (isFocused) {
      onRequestSort(updatedHeaders);
    }
  };

  /**
   * Renders the complete table header with interactive column headers
   * 
   * Dynamically generates table header cells based on column configuration,
   * creating sophisticated interactive headers with sorting, filtering, and
   * visual feedback capabilities.
   * 
   * Rendering Logic:
   * - Iterates through column header configuration array
   * - Creates JSX elements for each visible column (vis === 1)
   * - Applies appropriate styling and event handlers
   * - Returns array of configured TableCell components
   * 
   * Column Types:
   * - Sortable + Filterable: Full interactive headers with TextField and sort controls
   * - Sortable Only: Simple headers with TableSortLabel for sorting
   * - Display Only: Plain text labels without interactive elements
   * 
   * Interactive Components:
   * - TextField: Filter input with Material-UI styling
   * - TableSortLabel: Sort direction indicator with click handling
   * - FilterAltIcon: Animated filter toggle with rotation effects
   * - Tooltip: User guidance for filter functionality
   * 
   * Dynamic Behavior:
   * - Focus state management for filter inputs
   * - Sort direction indicators (asc/desc arrows)
   * - Filter icon rotation based on active state
   * - Label shrinking and expanding based on input state
   * 
   * Styling Features:
   * - Consistent TableCell styling from useStyles
   * - Custom TextField styling with transparent backgrounds
   * - Hover effects and focus indicators
   * - Responsive layout with flexible widths
   * 
   * Event Integration:
   * - Click handlers for sorting functionality
   * - Focus/blur handlers for filter state management
   * - Keyboard handlers for filter input processing
   * - Icon click handlers for filter toggle
   * 
   * Accessibility:
   * - Tooltip guidance for user interactions
   * - Proper label associations for form inputs
   * - Keyboard navigation support
   * - Clear visual feedback for interactive elements
   * 
   * @returns {React.JSX.Element[]} Array of configured TableCell components
   */
  /*
  * Build the menu items parent nodes
  */
  const renderColumnHeader = () => {
    const JSXColumnHeader: React.JSX.Element[] = [];
    tableHeaders.forEach((row: TableHeaders) => {
      const label = row.lb;
      switch (true) {
        default: {
          row.vis === 1 ? JSXColumnHeader.push(
            // Your TableCell component
            <TableCell
              className={classes.tableCell}
              key={row.id}
              align="left"
              padding="none"
            >
              <Tooltip
                title={'Skriv inn og trykk ENTER for å filtrere etter ' + label}
                //placement={row.numeric ? "bottom-end" : "bottom-start"}
                enterDelay={300}
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: 15,
                      fontWeight: 100,
                      color: '#fff', // Text color inside the tooltip
                    },
                  },
                }}
              >
                {row.isSort || row.isFilter ? (
                  <Box display="flex" alignItems="center">
                    <TextField
                      id={row.id}
                      label={label}
                      autoComplete="off"
                      variant="standard"
                      autoSave='off'
                      placeholder={methods.getValues(row.id as string)}
                      {...methods.register(row.id as string, { required: false })}
                      onFocus={(e) => handleFieldFocus(e, row)}
                      onBlur={(e) => handleFieldBlur(e, row)}
                      onKeyUp={(e) => { handleChange(e, row); }}
                      InputProps={{
                        disableUnderline: /*row.foc === 1 ? true : */true,
                        sx: {
                          height: '15px',
                          padding: '0px 0',
                          color: '#000',
                          //color: row.filter !== '' ? '#000' : '#000', // black color for the label when focused
                          '&:before': {
                            borderBottom: '1px solid lightgray', // Soft gray underline when unfocused
                          },
                          '&:after': {
                            borderBottom: '1px solid lightgray', // Soft gray underline when focused
                          },
                          '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '1px solid gray', // Slightly darker gray on hover, if desired
                          },
                        },
                      }}
                      sx={{
                        width: '95%',
                        '& .MuiInputLabel-root': {
                          top: '-16px',
                          fontWeight: 500,
                        },
                        '& .MuiInputLabel-shrink': {
                          color: '#000',
                          top: '-3px',
                          fontWeight: 400,
                        },
                        '& .MuiInputBase-root': {
                          marginBottom: '0px',
                        },
                      }}
                      InputLabelProps={{
                        shrink: row.foc === 1 ? true : false,
                        onClick: (e) => {
                          handleLabelClicked(e, row);
                        },
                        sx: {
                          '&.Mui-focused': {
                            cursor: 'pointer',
                            color: '#000',
                            fontSize: 17,
                          },
                        },
                      }}
                    />
                    <TableSortLabel
                      active={row.isActive === 1 ? true : false}
                      direction={row.sortDirection}
                      onClick={() => handleClick(row)}
                      hideSortIcon={true}

                    /> <FilterAltIcon onClick={(e) => handleLabelFilterClicked(e, row)}
                      sx={{
                        cursor: 'pointer',
                        color: '#darkgray',
                        transform: row.foc === 1 ? 'rotate(180deg)' : 'rotate(0deg)', // Rotate icon when active
                        transition: 'transform 0.3s ease', // Smooth transition for rotation
                        '&:before': { borderBottom: '1px solid lightgray', },
                        '&:after': { borderBottom: '1px solid lightgray', },
                        '&:hover:not(.Mui-disabled):before': { borderBottom: '1px solid gray', },
                      }}
                    />
                  </Box>
                ) : (
                  row.isSort && !row.isFilter ? <TableSortLabel
                    active={row.isActive === 1 ? true : false}
                    direction={row.sortDirection}
                    onClick={() => handleClick(row)}
                    hideSortIcon={false}
                  >{label}</TableSortLabel> : <span>{label}</span>
                )}
              </Tooltip>
            </TableCell>
          ) : null
        }
          break;
      }
    });
    //
    return JSXColumnHeader;
  };
  //
  return (
    <TableHead><TableRow>{renderColumnHeader()}</TableRow></TableHead>
  );
};

export default NN05TableHead;